// OnlineTables.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  Badge,
  Tooltip,
  Divider,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Collapse,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  Skeleton
} from '@mui/material';
import {
  SportsEsports as GameIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  VideogameAsset as XOIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  NotificationsActive as AlertIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  MoreVert as MoreVertIcon,
  GroupAdd as GroupIcon,
  WhatsApp as WhatsAppIcon,
  Notifications as NotifyIcon,
  MenuBook as MenuIcon,
  Restaurant as TableIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  Timer as TimerIcon,
  Speed as SpeedIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const OnlineTables = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { socket, isConnected } = useChat();
  const { user } = useAuth();
  
  const [onlineTables, setOnlineTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, playing, waiting, vip
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    playing: 0,
    waiting: 0,
    vip: 0
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const refreshInterval = useRef(null);

  // Fetch online tables
  const fetchOnlineTables = useCallback(async () => {
    try {
      const response = await api.get('/game/online-tables');
      const tables = response.data.tables || [];
      
      // Process and sort tables
      const processedTables = tables
        .map(table => ({
          ...table,
          isPlaying: table.gameStatus === 'playing',
          isWaiting: table.gameStatus === 'waiting',
          searchableName: `${table.tableNumber} ${table.playerName} ${table.tableName}`.toLowerCase()
        }))
        .sort((a, b) => {
          // VIP first, then by status (playing > waiting), then by table number
          if (a.isVip && !b.isVip) return -1;
          if (!a.isVip && b.isVip) return 1;
          if (a.isPlaying && !b.isPlaying) return -1;
          if (!a.isPlaying && b.isPlaying) return 1;
          return a.tableNumber - b.tableNumber;
        });
      
      setOnlineTables(processedTables);
      
      // Update stats
      setStats({
        total: processedTables.length,
        playing: processedTables.filter(t => t.isPlaying).length,
        waiting: processedTables.filter(t => t.isWaiting).length,
        vip: processedTables.filter(t => t.isVip).length
      });
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching online tables:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch online tables',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Setup socket listeners
  useEffect(() => {
    if (!socket) return;

    // Listen for player status updates
    socket.on('playerStatusUpdate', (data) => {
      console.log('Player status update:', data);
      fetchOnlineTables(); // Refresh the list
    });

    socket.on('gameStarted', (data) => {
      setSnackbar({
        open: true,
        message: `Game started between Table ${data.player1} and Table ${data.player2}`,
        severity: 'info'
      });
      fetchOnlineTables();
    });

    socket.on('gameEnded', (data) => {
      setSnackbar({
        open: true,
        message: data.winner ? `${data.winner} won the game!` : 'Game ended',
        severity: 'info'
      });
      fetchOnlineTables();
    });

    return () => {
      socket.off('playerStatusUpdate');
      socket.off('gameStarted');
      socket.off('gameEnded');
    };
  }, [socket, fetchOnlineTables]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    fetchOnlineTables();
    
    if (autoRefresh) {
      refreshInterval.current = setInterval(fetchOnlineTables, 10000);
    }
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh, fetchOnlineTables]);

  // Filter tables
  const filteredTables = onlineTables.filter(table => {
    if (filter === 'playing' && !table.isPlaying) return false;
    if (filter === 'waiting' && !table.isWaiting) return false;
    if (filter === 'vip' && !table.isVip) return false;
    if (searchQuery && !table.searchableName.includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Handle table actions
  const handleViewTable = (table) => {
    setSelectedTable(table);
    setAnchorEl(null);
    // Navigate to table details or open modal
  };

  const handleSendNotification = async (table) => {
    try {
      await api.post('/game/notify-player', { tableId: table.tableId });
      setSnackbar({
        open: true,
        message: `Notification sent to Table ${table.tableNumber}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send notification',
        severity: 'error'
      });
    }
    setAnchorEl(null);
  };

  const handleKickPlayer = async (table) => {
    if (window.confirm(`Are you sure you want to kick Table ${table.tableNumber}?`)) {
      try {
        await api.post('/game/kick-player', { tableId: table.tableId });
        setSnackbar({
          open: true,
          message: `Table ${table.tableNumber} has been kicked`,
          severity: 'warning'
        });
        fetchOnlineTables();
      } catch (error) {
        console.error('Error kicking player:', error);
        setSnackbar({
          open: true,
          message: 'Failed to kick player',
          severity: 'error'
        });
      }
    }
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, table) => {
    setSelectedTable(table);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTable(null);
  };

  // Status Badge Component
  const StatusBadge = ({ isPlaying, isWaiting }) => {
    if (isPlaying) {
      return (
        <Chip
          icon={<GameIcon sx={{ fontSize: 14 }} />}
          label="In Game"
          size="small"
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            fontWeight: 600,
            height: 24,
            '& .MuiChip-icon': { color: '#000000' }
          }}
        />
      );
    }
    if (isWaiting) {
      return (
        <Chip
          icon={<PeopleIcon sx={{ fontSize: 14 }} />}
          label="Waiting"
          size="small"
          sx={{
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            color: '#42A5F5',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            fontWeight: 600,
            height: 24
          }}
        />
      );
    }
    return (
      <Chip
        label="Idle"
        size="small"
        sx={{
          backgroundColor: 'rgba(158, 158, 158, 0.2)',
          color: '#9E9E9E',
          fontWeight: 600,
          height: 24
        }}
      />
    );
  };

  // Table Card Component
  const TableCard = ({ table, index }) => {
    return (
      <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
        <Card
          component={motion.div}
          whileHover={{ y: -4, transition: { duration: 0.2 } }}
          sx={{
            backgroundColor: '#0D0D0D',
            border: table.isVip 
              ? '2px solid rgba(255, 215, 0, 0.5)' 
              : '1px solid rgba(255, 215, 0, 0.15)',
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#FFD700',
              boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)'
            }
          }}
        >
          {/* VIP Badge */}
          {table.isVip && (
            <Box sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
            }}>
              <Tooltip title="VIP Table">
                <StarIcon sx={{ color: '#FFD700', fontSize: 20 }} />
              </Tooltip>
            </Box>
          )}
          
          <CardContent sx={{ p: 2.5 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#FFFFFF'
                }}>
                  <TableIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                  Table {table.tableNumber}
                  {table.tableName && (
                    <Typography component="span" sx={{ 
                      fontSize: '0.75rem', 
                      color: '#B3B3B3',
                      fontWeight: 400
                    }}>
                      ({table.tableName})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ color: '#888888', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  {isConnected ? <WifiIcon sx={{ fontSize: 12, color: '#4CAF50' }} /> : <WifiOffIcon sx={{ fontSize: 12, color: '#F44336' }} />}
                  Player: {table.playerName || 'Anonymous'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, table)}
                sx={{ color: '#B3B3B3' }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>

            {/* Game Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5,
              backgroundColor: '#1A1A1A',
              borderRadius: '12px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <XOIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  XO Game
                </Typography>
              </Box>
              <StatusBadge isPlaying={table.isPlaying} isWaiting={table.isWaiting} />
            </Box>

            {/* Game Stats */}
            {table.isPlaying && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#888888', display: 'block', mb: 0.5 }}>
                  Game Progress
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={table.gameProgress || 50}
                  sx={{
                    backgroundColor: '#1A1A1A',
                    borderRadius: 10,
                    height: 6,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#FFD700',
                      borderRadius: 10
                    }
                  }}
                />
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={() => handleViewTable(table)}
                sx={{
                  borderColor: '#2D2D2D',
                  color: '#FFFFFF',
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#FFD700',
                    backgroundColor: 'rgba(255, 215, 0, 0.08)'
                  }
                }}
              >
                View
              </Button>
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<NotifyIcon />}
                onClick={() => handleSendNotification(table)}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#FFE55C'
                  }
                }}
              >
                Notify
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Zoom>
    );
  };

  // Stats Card Component
  const StatCard = ({ title, value, icon, color }) => (
    <Paper
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      sx={{
        p: 2,
        backgroundColor: '#0D0D0D',
        border: '1px solid rgba(255, 215, 0, 0.15)',
        borderRadius: '16px',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: color,
          boxShadow: `0 4px 12px ${color}20`
        }
      }}
    >
      <Typography variant="h3" sx={{ fontWeight: 800, color: color, fontSize: '2rem' }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {icon} {title}
      </Typography>
    </Paper>
  );

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={200} sx={{ bgcolor: '#1A1A1A' }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#0A0A0A',
      pt: 3,
      pb: 6
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            component={motion.h1}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            variant="h4"
            sx={{
              fontWeight: 900,
              background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Online Gaming Tables
          </Typography>
          <Typography variant="body2" sx={{ color: '#888888' }}>
            Monitor and manage active game tables in real-time
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <StatCard 
              title="Total Tables" 
              value={stats.total} 
              icon={<TableIcon sx={{ fontSize: 16 }} />}
              color="#FFD700"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard 
              title="In Game" 
              value={stats.playing} 
              icon={<GameIcon sx={{ fontSize: 16 }} />}
              color="#4CAF50"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard 
              title="Waiting" 
              value={stats.waiting} 
              icon={<PeopleIcon sx={{ fontSize: 16 }} />}
              color="#42A5F5"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard 
              title="VIP Tables" 
              value={stats.vip} 
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              color="#FF9800"
            />
          </Grid>
        </Grid>

        {/* Controls Bar */}
        <Paper sx={{
          p: 2,
          mb: 3,
          backgroundColor: '#0D0D0D',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          borderRadius: '16px'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by table or player..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#888888' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: '#1A1A1A',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: '#2D2D2D' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={filter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setFilter('all')}
                  sx={{
                    flex: 1,
                    borderRadius: '12px',
                    textTransform: 'none',
                    ...(filter === 'all' ? {
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      '&:hover': { backgroundColor: '#FFE55C' }
                    } : {
                      borderColor: '#2D2D2D',
                      color: '#FFFFFF'
                    })
                  }}
                >
                  All
                </Button>
                <Button
                  variant={filter === 'playing' ? 'contained' : 'outlined'}
                  onClick={() => setFilter('playing')}
                  sx={{
                    flex: 1,
                    borderRadius: '12px',
                    textTransform: 'none',
                    ...(filter === 'playing' ? {
                      backgroundColor: '#4CAF50',
                      color: '#FFFFFF',
                      '&:hover': { backgroundColor: '#45A049' }
                    } : {
                      borderColor: '#2D2D2D',
                      color: '#FFFFFF'
                    })
                  }}
                >
                  Playing
                </Button>
                <Button
                  variant={filter === 'waiting' ? 'contained' : 'outlined'}
                  onClick={() => setFilter('waiting')}
                  sx={{
                    flex: 1,
                    borderRadius: '12px',
                    textTransform: 'none',
                    ...(filter === 'waiting' ? {
                      backgroundColor: '#42A5F5',
                      color: '#FFFFFF',
                      '&:hover': { backgroundColor: '#1E88E5' }
                    } : {
                      borderColor: '#2D2D2D',
                      color: '#FFFFFF'
                    })
                  }}
                >
                  Waiting
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#FFD700',
                          '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.08)' }
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#FFD700'
                        }
                      }}
                    />
                  }
                  label="Auto-refresh"
                  sx={{ color: '#B3B3B3' }}
                />
                <Tooltip title="Refresh now">
                  <IconButton 
                    onClick={fetchOnlineTables}
                    sx={{ color: '#FFD700' }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Last Update Time */}
        <Typography variant="caption" sx={{ color: '#666666', display: 'block', mb: 2, textAlign: 'right' }}>
          Last updated: {lastUpdate.toLocaleTimeString()}
          {autoRefresh && ' (auto-refreshing every 10s)'}
        </Typography>

        {/* Tables Grid */}
        {filteredTables.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <GameIcon sx={{ fontSize: 64, color: '#2D2D2D', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#888888' }}>
              No online tables found
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              {searchQuery ? 'Try adjusting your search' : 'Waiting for players to join'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <AnimatePresence>
              {filteredTables.map((table, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={table.tableId || index}>
                  <TableCard table={table} index={index} />
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Menu for table actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: '#1A1A1A',
              border: '1px solid #2D2D2D',
              borderRadius: '12px',
              minWidth: 180
            }
          }}
        >
          <MenuItem onClick={() => handleViewTable(selectedTable)}>
            <ListItemIcon>
              <ViewIcon sx={{ color: '#FFD700', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="View Details" />
          </MenuItem>
          <MenuItem onClick={() => handleSendNotification(selectedTable)}>
            <ListItemIcon>
              <NotifyIcon sx={{ color: '#42A5F5', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Send Notification" />
          </MenuItem>
          <Divider sx={{ backgroundColor: '#2D2D2D' }} />
          <MenuItem onClick={() => handleKickPlayer(selectedTable)} sx={{ color: '#F44336' }}>
            <ListItemIcon>
              <BlockIcon sx={{ color: '#F44336', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Kick Player" />
          </MenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity}
            sx={{
              backgroundColor: '#0D0D0D',
              color: '#FFFFFF',
              border: `1px solid ${snackbar.severity === 'success' ? '#FFD700' : '#F44336'}`,
              borderRadius: '12px'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default OnlineTables;